import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldAlert, Skull, Cog, Plus, Search, Trash2, Pencil, Loader2, RotateCcw, Lock, ChevronDown, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { Panel, StatTile } from "@/components/common/Panel";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { SeverityBadge, ProvenanceBadge } from "@/components/common/Badges";
import { FunctionDetailBlock } from "@/components/roles/FunctionDrilldown";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const LEVELS = ["critical", "high", "medium", "low"];

function Field({ label, value, onChange, placeholder, disabled, mono = true }) {
    return (
        <div>
            <label className="label-eyebrow block mb-1">{label}</label>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                className={`field w-full disabled:opacity-50 ${mono ? "font-mono" : ""}`}
            />
        </div>
    );
}

function LevelSelect({ value, onChange }) {
    return (
        <div>
            <label className="label-eyebrow block mb-1">Severity</label>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="field w-full">
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
        </div>
    );
}

/** Maps a GrcFunction API doc onto the shared FunctionDetailBlock shape. */
const toDetailFn = (f) => ({
    function_id: f.function_id,
    name: f.name,
    bp: f.bp,
    actions: f.actions || [],
    objects: f.objects || [],
    object_fields: f.object_fields || {},
    object_sources: f.object_sources || {},
    provenance: f.provenance,
    enrichment: f.enrichment || {},
});

/** object_fields dict → editable rows [{object, field, values}] and back. */
const fieldsToRows = (objectFields) => {
    const rows = [];
    Object.entries(objectFields || {}).forEach(([obj, fields]) => {
        Object.entries(fields || {}).forEach(([field, values]) => {
            rows.push({ object: obj, field, values: (values || []).join(", ") });
        });
    });
    return rows;
};
const rowsToFields = (rows) => {
    const out = {};
    rows.forEach((r) => {
        const obj = r.object.trim().toUpperCase();
        const field = r.field.trim().toUpperCase();
        const values = r.values.split(",").map((v) => v.trim()).filter(Boolean);
        if (!obj || !field || values.length === 0) return;
        out[obj] = out[obj] || {};
        out[obj][field] = values;
    });
    return out;
};

/** Shared Create/Edit Function dialog — T-codes, Authorization Objects AND
 * permission field values (the lowest drill-down level) are all editable.
 * Opened from the Functions tab and from the Risk/Critical drill-downs. */
function FunctionEditorDialog({ open, onOpenChange, editing, reload }) {
    const [form, setForm] = useState({ function_id: "", name: "", bp: "General", actions: "", objects: "" });
    const [fieldRows, setFieldRows] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setForm({
                function_id: editing.function_id,
                name: editing.name,
                bp: editing.bp,
                actions: (editing.actions || []).join(", "),
                objects: (editing.objects || []).join(", "),
            });
            setFieldRows(fieldsToRows(editing.object_fields));
        } else {
            setForm({ function_id: "", name: "", bp: "General", actions: "", objects: "" });
            setFieldRows([]);
        }
    }, [open, editing]);

    const setRow = (i, patch) => setFieldRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    const addRow = () => setFieldRows((prev) => [...prev, { object: "", field: "ACTVT", values: "" }]);
    const removeRow = (i) => setFieldRows((prev) => prev.filter((_, idx) => idx !== i));

    const submit = async () => {
        if (!form.function_id.trim() || !form.name.trim()) { toast.error("Function ID and name are required"); return; }
        const payload = {
            name: form.name.trim(), bp: form.bp.trim() || "General",
            actions: form.actions.split(",").map((t) => t.trim()).filter(Boolean),
            objects: form.objects.split(",").map((t) => t.trim()).filter(Boolean),
            object_fields: rowsToFields(fieldRows),
        };
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/admin/ruleset/functions/${editing.function_id}`, payload);
                toast.success("Function updated — live across every analysis engine");
            } else {
                await api.post("/admin/ruleset/functions", { ...payload, function_id: form.function_id.trim().toUpperCase() });
                toast.success("Function added");
            }
            onOpenChange(false);
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[var(--bg-elevated)] border border-cyan-500/20 text-slate-200 max-w-xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-white">{editing ? `Edit Function · ${editing.function_id}` : "Add Function"}</DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 font-mono">
                        Business function — T-codes, authorization objects and permission field values that SoD risks are built from.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Function ID" value={form.function_id} onChange={(v) => setForm({ ...form, function_id: v })} placeholder="AO99" disabled={!!editing} />
                        <Field label="Business Process" value={form.bp} onChange={(v) => setForm({ ...form, bp: v })} placeholder="Procure to Pay" mono={false} />
                    </div>
                    <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Maintain Purchase Orders" mono={false} />
                    <Field label="T-codes / Fiori App IDs (comma-separated)" value={form.actions} onChange={(v) => setForm({ ...form, actions: v })} placeholder="ME21N, ME22N" />
                    <Field label="Authorization Objects (comma-separated)" value={form.objects} onChange={(v) => setForm({ ...form, objects: v })} placeholder="M_BEST_BSA, M_BEST_EKO" />

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="label-eyebrow">Permission Field Values (Object · Field · Values)</label>
                            <button data-testid="ruleset-fn-add-field-row" onClick={addRow} className="text-[11px] font-mono text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1">
                                <Plus size={11} /> Add value row
                            </button>
                        </div>
                        {fieldRows.length === 0 && (
                            <p className="text-[11px] text-slate-500 font-mono">No field values captured — add rows (e.g. F_BKPF_BUK · ACTVT · 01, 02) to enable field-verified SoD matching.</p>
                        )}
                        <div className="space-y-1.5">
                            {fieldRows.map((r, i) => (
                                <div key={i} className="flex items-center gap-2" data-testid={`ruleset-fn-field-row-${i}`}>
                                    <input value={r.object} onChange={(e) => setRow(i, { object: e.target.value })} placeholder="F_BKPF_BUK" className="field font-mono flex-[2]" />
                                    <input value={r.field} onChange={(e) => setRow(i, { field: e.target.value })} placeholder="ACTVT" className="field font-mono flex-1" />
                                    <input value={r.values} onChange={(e) => setRow(i, { values: e.target.value })} placeholder="01, 02" className="field font-mono flex-[2]" />
                                    <button onClick={() => removeRow(i)} className="text-slate-500 hover:text-rose-400" aria-label="Remove value row"><X size={13} /></button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <button data-testid={TID.rulesetFormSubmit} onClick={submit} disabled={saving} className="btn-primary disabled:opacity-50 inline-flex items-center gap-2">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : null}{editing ? "Save Changes" : "Add Function"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** Read-only function drill-down + jump-to-edit, shared by the Risk and
 * Critical Activity expansions. */
function FunctionDrillCard({ fn, onEdit }) {
    if (!fn) return <p className="text-[11px] text-rose-300 font-mono">Function reference is dangling — it was removed from the ruleset.</p>;
    return (
        <div className="relative">
            <FunctionDetailBlock fn={toDetailFn(fn)} />
            <button
                data-testid={`ruleset-drill-edit-${fn.function_id}`}
                onClick={(e) => { e.stopPropagation(); onEdit(fn); }}
                className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-mono text-cyan-400 hover:text-cyan-300"
                title="Edit this function's T-codes, objects & field values"
            >
                <Pencil size={10} /> Edit
            </button>
        </div>
    );
}

export default function RulesetConfig() {
    const { user } = useAuth();
    const isAdmin = (user?.roles || []).includes("Admin");
    const [functions, setFunctions] = useState([]);
    const [risks, setRisks] = useState([]);
    const [criticals, setCriticals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resetting, setResetting] = useState(false);
    // Shared function editor — openable from any tab's drill-down.
    const [fnEditorOpen, setFnEditorOpen] = useState(false);
    const [fnEditing, setFnEditing] = useState(null);

    const load = useCallback(async () => {
        try {
            const [f, r, c] = await Promise.all([
                api.get("/admin/ruleset/functions"),
                api.get("/admin/ruleset/risks"),
                api.get("/admin/ruleset/critical-activities"),
            ]);
            setFunctions(f.data);
            setRisks(r.data);
            setCriticals(c.data);
        } catch (e) {
            console.warn("Ruleset config load failed (Admin role required)", e);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [load, isAdmin]);

    const openFnEditor = (fn) => { setFnEditing(fn); setFnEditorOpen(true); };

    const resetToDefault = async () => {
        if (!confirm("This wipes ALL admin edits to the SoD Ruleset and restores the original imported GRC export. Continue?")) return;
        setResetting(true);
        try {
            await api.post("/admin/ruleset/reset");
            toast.success("Ruleset restored to default");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed");
        } finally { setResetting(false); }
    };

    if (!isAdmin) {
        return (
            <Panel eyebrow="Restricted" title="Ruleset Configuration">
                <p className="text-sm text-slate-400 flex items-center gap-2">
                    <Lock size={14} /> You do not have the <span className="font-mono text-cyan-300">Admin</span> role.
                    Editing the live SoD Ruleset is reserved for platform owners.
                </p>
            </Panel>
        );
    }

    return (
        <div className="space-y-6" data-testid="ruleset-config-page">
            <div className="panel-elevated relative overflow-hidden p-6">
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-rose-500/10 blur-3xl pointer-events-none" />
                <div className="flex items-center justify-between gap-4 relative flex-wrap">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-cyan-300 to-cyan-600 flex items-center justify-center shadow-[0_0_20px_rgb(var(--accent-1-rgb) / 0.45)]">
                            <Cog size={22} className="text-slate-950" strokeWidth={2.5} />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl text-white tracking-wide">Ruleset Configuration</h1>
                            <p className="text-sm text-slate-400 mt-0.5">
                                Admin-only. Expand any Risk, Critical Activity or Function to drill down — and modify — its T-codes, Authorization Objects &amp; field values. Changes apply live across every analysis engine.
                            </p>
                        </div>
                    </div>
                    <button data-testid={TID.rulesetResetBtn} onClick={resetToDefault} disabled={resetting} className="btn-ghost inline-flex items-center gap-2 disabled:opacity-50">
                        {resetting ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Reset to Default
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
                <StatTile testId={TID.statTile("ruleset-risks")} label="SoD Risks" value={risks.length} sub="conflict pairs" icon={ShieldAlert} tone="red" />
                <StatTile testId={TID.statTile("ruleset-criticals")} label="Critical Activities" value={criticals.length} sub="standalone" icon={Skull} tone="amber" />
                <StatTile testId={TID.statTile("ruleset-functions")} label="Functions" value={functions.length} sub="tcode/object groups" icon={Cog} tone="cyan" />
            </div>

            {loading ? (
                <p className="text-sm text-slate-500 font-mono py-6 text-center">Loading ruleset…</p>
            ) : (
                <Tabs defaultValue="risks">
                    <TabsList>
                        <TabsTrigger data-testid={TID.rulesetTab("risks")} value="risks">SoD Risks ({risks.length})</TabsTrigger>
                        <TabsTrigger data-testid={TID.rulesetTab("criticals")} value="criticals">Critical Activities ({criticals.length})</TabsTrigger>
                        <TabsTrigger data-testid={TID.rulesetTab("functions")} value="functions">Functions ({functions.length})</TabsTrigger>
                    </TabsList>
                    <TabsContent value="risks"><RisksTab risks={risks} functions={functions} reload={load} openFnEditor={openFnEditor} /></TabsContent>
                    <TabsContent value="criticals"><CriticalsTab criticals={criticals} functions={functions} reload={load} openFnEditor={openFnEditor} /></TabsContent>
                    <TabsContent value="functions"><FunctionsTab functions={functions} reload={load} openFnEditor={openFnEditor} /></TabsContent>
                </Tabs>
            )}

            <FunctionEditorDialog open={fnEditorOpen} onOpenChange={setFnEditorOpen} editing={fnEditing} reload={load} />
        </div>
    );
}

// ---------------- Risks Tab ----------------
const emptyRisk = { risk_id: "", name: "", bp: "General", level: "high", functions: ["", ""], description: "", business_impact: "" };

function RisksTab({ risks, functions, reload, openFnEditor }) {
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyRisk);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const fnById = (fid) => functions.find((f) => f.function_id === fid);
    const fnName = (fid) => fnById(fid)?.name || fid;

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return risks;
        return risks.filter((r) => r.risk_id.toLowerCase().includes(q) || r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q));
    }, [risks, search]);

    const openCreate = () => { setForm(emptyRisk); setEditingId(null); setOpen(true); };
    const openEdit = (r) => { setForm({ ...r }); setEditingId(r.risk_id); setOpen(true); };

    const submit = async () => {
        if (!form.risk_id.trim() || !form.name.trim() || !form.functions[0] || !form.functions[1]) {
            toast.error("Risk ID, name and both conflicting functions are required"); return;
        }
        setSaving(true);
        try {
            if (editingId) {
                await api.put(`/admin/ruleset/risks/${editingId}`, form);
                toast.success("Risk updated");
            } else {
                await api.post("/admin/ruleset/risks", { ...form, risk_id: form.risk_id.trim().toUpperCase() });
                toast.success("Risk added to the ruleset");
            }
            setOpen(false); await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const remove = async (r) => {
        if (!confirm(`Delete risk ${r.risk_id}? This stops it being detected in every future analysis.`)) return;
        try {
            await api.delete(`/admin/ruleset/risks/${r.risk_id}`);
            toast.success("Risk removed"); await reload();
        } catch (e) { toast.error("Delete failed"); }
    };

    return (
        <Panel eyebrow="SoD Rulebook" title={`Risk conflict pairs (${filtered.length})`}
            action={<button data-testid={TID.rulesetAddBtn} onClick={openCreate} className="btn-primary sheen inline-flex items-center gap-2"><Plus size={14} /> Add Risk</button>}>
            <div className="relative mb-4">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid={TID.rulesetSearchInput} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search risk id, name or description…" className="w-full bg-[var(--bg-base)] border border-white/10 rounded-sm pl-8 pr-3 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none" />
            </div>
            <div className="space-y-2 max-h-[560px] overflow-y-auto">
                {filtered.map((r) => {
                    const isOpen = expanded === r.risk_id;
                    return (
                    <div key={r.risk_id} data-testid={TID.rulesetRiskRow(r.risk_id)} className="panel p-3 rounded-[4px]">
                        <div className="flex items-start gap-3">
                            <button
                                data-testid={`ruleset-risk-expand-${r.risk_id}`}
                                onClick={() => setExpanded(isOpen ? null : r.risk_id)}
                                className="text-slate-500 hover:text-cyan-400 transition-colors mt-0.5 shrink-0"
                                aria-label={`Drill down ${r.risk_id}`}
                            >
                                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(isOpen ? null : r.risk_id)}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-[11px] text-cyan-300">{r.risk_id}</span>
                                    <SeverityBadge severity={r.level} />
                                    <span className="text-xs text-white font-medium">{r.name}</span>
                                </div>
                                <div className="text-[11px] font-mono text-slate-500 mt-1">{fnName(r.functions[0])} ↔ {fnName(r.functions[1])}</div>
                                {r.description && r.description.trim() !== r.name.trim() && (
                                    <p className="text-[11px] text-slate-400 mt-1">{r.description}</p>
                                )}
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button data-testid={TID.rulesetEditBtn(r.risk_id)} onClick={() => openEdit(r)} className="text-slate-500 hover:text-cyan-400"><Pencil size={13} /></button>
                                <button data-testid={TID.rulesetDeleteBtn(r.risk_id)} onClick={() => remove(r)} className="text-slate-500 hover:text-rose-400"><Trash2 size={13} /></button>
                            </div>
                        </div>
                        {isOpen && (
                            <div className="mt-3 pt-3 border-t border-white/5" data-testid={`ruleset-risk-drilldown-${r.risk_id}`}>
                                {r.business_impact && (
                                    <p className="text-[11px] text-amber-200/80 mb-2"><strong className="text-amber-300">Business impact:</strong> {r.business_impact}</p>
                                )}
                                <div className="grid md:grid-cols-2 gap-2">
                                    <FunctionDrillCard fn={fnById(r.functions[0])} onEdit={openFnEditor} />
                                    <FunctionDrillCard fn={fnById(r.functions[1])} onEdit={openFnEditor} />
                                </div>
                            </div>
                        )}
                    </div>
                    );
                })}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-[var(--bg-elevated)] border border-cyan-500/20 text-slate-200 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display text-white">{editingId ? "Edit SoD Risk" : "Add SoD Risk"}</DialogTitle>
                        <DialogDescription className="text-xs text-slate-500 font-mono">
                            Segregation-of-duties risk definition — conflicting function pair, risk level and business rationale.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Risk ID" value={form.risk_id} onChange={(v) => setForm({ ...form, risk_id: v })} placeholder="P2P099" disabled={!!editingId} />
                            <Field label="Business Process" value={form.bp} onChange={(v) => setForm({ ...form, bp: v })} placeholder="Procure to Pay" mono={false} />
                        </div>
                        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Create Vendor & Post Invoice" mono={false} />
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="label-eyebrow block mb-1">Function A</label>
                                <select value={form.functions[0]} onChange={(e) => setForm({ ...form, functions: [e.target.value, form.functions[1]] })} className="field w-full">
                                    <option value="">Select…</option>
                                    {functions.map((f) => <option key={f.function_id} value={f.function_id}>{`${f.function_id} — ${f.name}`}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="label-eyebrow block mb-1">Function B</label>
                                <select value={form.functions[1]} onChange={(e) => setForm({ ...form, functions: [form.functions[0], e.target.value] })} className="field w-full">
                                    <option value="">Select…</option>
                                    {functions.map((f) => <option key={f.function_id} value={f.function_id}>{`${f.function_id} — ${f.name}`}</option>)}
                                </select>
                            </div>
                        </div>
                        <LevelSelect value={form.level} onChange={(v) => setForm({ ...form, level: v })} />
                        <div><label className="label-eyebrow block mb-1">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="field w-full resize-y" /></div>
                        <div><label className="label-eyebrow block mb-1">Business Impact</label><textarea value={form.business_impact} onChange={(e) => setForm({ ...form, business_impact: e.target.value })} rows={2} className="field w-full resize-y" /></div>
                    </div>
                    <DialogFooter>
                        <button data-testid={TID.rulesetFormSubmit} onClick={submit} disabled={saving} className="btn-primary disabled:opacity-50 inline-flex items-center gap-2">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : null}{editingId ? "Save Changes" : "Add Risk"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Panel>
    );
}

// ---------------- Critical Activities Tab ----------------
const emptyCritical = { risk_id: "", name: "", bp: "General", level: "high", function: "", description: "", business_impact: "" };

function CriticalsTab({ criticals, functions, reload, openFnEditor }) {
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyCritical);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const fnById = (fid) => functions.find((f) => f.function_id === fid);
    const fnName = (fid) => fnById(fid)?.name || fid;

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return criticals;
        return criticals.filter((c) => c.risk_id.toLowerCase().includes(q) || c.name.toLowerCase().includes(q));
    }, [criticals, search]);

    const openCreate = () => { setForm(emptyCritical); setEditingId(null); setOpen(true); };
    const openEdit = (c) => { setForm({ ...c }); setEditingId(c.risk_id); setOpen(true); };

    const submit = async () => {
        if (!form.risk_id.trim() || !form.name.trim() || !form.function) {
            toast.error("Risk ID, name and function are required"); return;
        }
        setSaving(true);
        try {
            if (editingId) {
                await api.put(`/admin/ruleset/critical-activities/${editingId}`, form);
                toast.success("Critical activity updated");
            } else {
                await api.post("/admin/ruleset/critical-activities", { ...form, risk_id: form.risk_id.trim().toUpperCase() });
                toast.success("Critical activity added");
            }
            setOpen(false); await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const remove = async (c) => {
        if (!confirm(`Delete critical activity ${c.risk_id}?`)) return;
        try {
            await api.delete(`/admin/ruleset/critical-activities/${c.risk_id}`);
            toast.success("Removed"); await reload();
        } catch (e) { toast.error("Delete failed"); }
    };

    return (
        <Panel eyebrow="Standalone Sensitive Access" title={`Critical activities (${filtered.length})`}
            action={<button data-testid={TID.rulesetAddBtn} onClick={openCreate} className="btn-primary sheen inline-flex items-center gap-2"><Plus size={14} /> Add Critical Activity</button>}>
            <div className="relative mb-4">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="w-full bg-[var(--bg-base)] border border-white/10 rounded-sm pl-8 pr-3 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none" />
            </div>
            <div className="space-y-2 max-h-[560px] overflow-y-auto">
                {filtered.map((c) => {
                    const isOpen = expanded === c.risk_id;
                    return (
                    <div key={c.risk_id} data-testid={TID.rulesetCriticalRow(c.risk_id)} className="panel p-3 rounded-[4px]">
                        <div className="flex items-start gap-3">
                            <button
                                data-testid={`ruleset-critical-expand-${c.risk_id}`}
                                onClick={() => setExpanded(isOpen ? null : c.risk_id)}
                                className="text-slate-500 hover:text-cyan-400 transition-colors mt-0.5 shrink-0"
                                aria-label={`Drill down ${c.risk_id}`}
                            >
                                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(isOpen ? null : c.risk_id)}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-[11px] text-cyan-300">{c.risk_id}</span>
                                    <SeverityBadge severity={c.level} />
                                    <span className="text-xs text-white font-medium">{c.name}</span>
                                </div>
                                <div className="text-[11px] font-mono text-slate-500 mt-1">{fnName(c.function)}</div>
                                {c.description && c.description.trim() !== c.name.trim() && (
                                    <p className="text-[11px] text-slate-400 mt-1">{c.description}</p>
                                )}
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button data-testid={TID.rulesetEditBtn(c.risk_id)} onClick={() => openEdit(c)} className="text-slate-500 hover:text-cyan-400"><Pencil size={13} /></button>
                                <button data-testid={TID.rulesetDeleteBtn(c.risk_id)} onClick={() => remove(c)} className="text-slate-500 hover:text-rose-400"><Trash2 size={13} /></button>
                            </div>
                        </div>
                        {isOpen && (
                            <div className="mt-3 pt-3 border-t border-white/5" data-testid={`ruleset-critical-drilldown-${c.risk_id}`}>
                                {c.business_impact && (
                                    <p className="text-[11px] text-amber-200/80 mb-2"><strong className="text-amber-300">Business impact:</strong> {c.business_impact}</p>
                                )}
                                <FunctionDrillCard fn={fnById(c.function)} onEdit={openFnEditor} />
                            </div>
                        )}
                    </div>
                    );
                })}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-[var(--bg-elevated)] border border-cyan-500/20 text-slate-200 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display text-white">{editingId ? "Edit Critical Activity" : "Add Critical Activity"}</DialogTitle>
                        <DialogDescription className="text-xs text-slate-500 font-mono">
                            A single high-risk capability that is critical on its own — no conflicting pair required.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Risk ID" value={form.risk_id} onChange={(v) => setForm({ ...form, risk_id: v })} placeholder="CRIT099" disabled={!!editingId} />
                            <Field label="Business Process" value={form.bp} onChange={(v) => setForm({ ...form, bp: v })} placeholder="Basis & Security" mono={false} />
                        </div>
                        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Maintain Bank Master Data" mono={false} />
                        <div>
                            <label className="label-eyebrow block mb-1">Function</label>
                            <select value={form.function} onChange={(e) => setForm({ ...form, function: e.target.value })} className="field w-full">
                                <option value="">Select…</option>
                                {functions.map((f) => <option key={f.function_id} value={f.function_id}>{`${f.function_id} — ${f.name}`}</option>)}
                            </select>
                        </div>
                        <LevelSelect value={form.level} onChange={(v) => setForm({ ...form, level: v })} />
                        <div><label className="label-eyebrow block mb-1">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="field w-full resize-y" /></div>
                        <div><label className="label-eyebrow block mb-1">Business Impact</label><textarea value={form.business_impact} onChange={(e) => setForm({ ...form, business_impact: e.target.value })} rows={2} className="field w-full resize-y" /></div>
                    </div>
                    <DialogFooter>
                        <button data-testid={TID.rulesetFormSubmit} onClick={submit} disabled={saving} className="btn-primary disabled:opacity-50 inline-flex items-center gap-2">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : null}{editingId ? "Save Changes" : "Add Critical Activity"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Panel>
    );
}

// ---------------- Functions Tab ----------------
function FunctionsTab({ functions, reload, openFnEditor }) {
    const [search, setSearch] = useState("");
    const [expanded, setExpanded] = useState(null);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return functions;
        return functions.filter((f) => f.function_id.toLowerCase().includes(q) || f.name.toLowerCase().includes(q) || f.actions.join(" ").toLowerCase().includes(q));
    }, [functions, search]);

    const remove = async (f) => {
        if (!confirm(`Delete function ${f.function_id}? Fails if any Risk or Critical Activity still references it.`)) return;
        try {
            await api.delete(`/admin/ruleset/functions/${f.function_id}`);
            toast.success("Removed"); await reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed — likely still referenced"); }
    };

    return (
        <Panel eyebrow="T-code / Auth-Object Groups" title={`Functions (${filtered.length})`}
            action={<button data-testid={TID.rulesetAddBtn} onClick={() => openFnEditor(null)} className="btn-primary sheen inline-flex items-center gap-2"><Plus size={14} /> Add Function</button>}>
            <div className="relative mb-4">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search function id, name or t-code…" className="w-full bg-[var(--bg-base)] border border-white/10 rounded-sm pl-8 pr-3 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none" />
            </div>
            <div className="space-y-2 max-h-[560px] overflow-y-auto">
                {filtered.map((f) => {
                    const isOpen = expanded === f.function_id;
                    const fieldValueCount = Object.keys(f.object_fields || {}).length;
                    return (
                    <div key={f.function_id} data-testid={TID.rulesetFunctionRow(f.function_id)} className="panel p-3 rounded-[4px]">
                        <div className="flex items-start gap-3">
                            <button
                                data-testid={`ruleset-function-expand-${f.function_id}`}
                                onClick={() => setExpanded(isOpen ? null : f.function_id)}
                                className="text-slate-500 hover:text-cyan-400 transition-colors mt-0.5 shrink-0"
                                aria-label={`Drill down ${f.function_id}`}
                            >
                                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(isOpen ? null : f.function_id)}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-[11px] text-cyan-300">{f.function_id}</span>
                                    <span className="text-xs text-white font-medium">{f.name}</span>
                                    <span className="text-[10px] font-mono text-slate-500">{f.bp}</span>
                                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">{(f.actions || []).length} t-codes</span>
                                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-400 border border-white/10">{(f.objects || []).length} objects</span>
                                    {fieldValueCount > 0 && (
                                        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">{fieldValueCount} field-valued</span>
                                    )}
                                    <ProvenanceBadge
                                        provenance={f.provenance}
                                        enrichment={f.enrichment}
                                        testId={`ruleset-function-provenance-${f.function_id}`}
                                    />
                                </div>
                                {!isOpen && (
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                        {(f.actions || []).slice(0, 12).map((a) => <span key={a} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">{a}</span>)}
                                        {(f.actions || []).length > 12 && <span className="text-[10px] font-mono text-slate-500">+{f.actions.length - 12} more…</span>}
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button data-testid={TID.rulesetEditBtn(f.function_id)} onClick={() => openFnEditor(f)} className="text-slate-500 hover:text-cyan-400"><Pencil size={13} /></button>
                                <button data-testid={TID.rulesetDeleteBtn(f.function_id)} onClick={() => remove(f)} className="text-slate-500 hover:text-rose-400"><Trash2 size={13} /></button>
                            </div>
                        </div>
                        {isOpen && (
                            <div className="mt-3 pt-3 border-t border-white/5" data-testid={`ruleset-function-drilldown-${f.function_id}`}>
                                <FunctionDetailBlock fn={toDetailFn(f)} />
                            </div>
                        )}
                    </div>
                    );
                })}
            </div>
        </Panel>
    );
}
